import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Layout } from "@/components/Layout";
import { SeoHead } from "@/components/SeoHead";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL as string;
const SUPABASE_ANON = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY as string;

const Unsubscribe = () => {
  const [params] = useSearchParams();
  const token = params.get("token");
  const [state, setState] = useState<"loading" | "valid" | "already" | "invalid" | "submitting" | "done" | "error">("loading");
  const [error, setError] = useState<string>("");

  useEffect(() => {
    if (!token) { setState("invalid"); return; }
    (async () => {
      try {
        const res = await fetch(
          `${SUPABASE_URL}/functions/v1/handle-email-unsubscribe?token=${encodeURIComponent(token)}`,
          { headers: { apikey: SUPABASE_ANON } }
        );
        const data = await res.json();
        if (data?.valid) setState("valid");
        else if (data?.reason === "already_unsubscribed") setState("already");
        else setState("invalid");
      } catch {
        setState("invalid");
      }
    })();
  }, [token]);

  const confirm = async () => {
    if (!token) return;
    setState("submitting");
    try {
      const { data, error: err } = await supabase.functions.invoke("handle-email-unsubscribe", { body: { token } });
      if (err) throw err;
      if (data?.success || data?.reason === "already_unsubscribed") setState("done");
      else { setError("Could not process unsubscribe."); setState("error"); }
    } catch (e: any) {
      setError(e?.message || "Unexpected error");
      setState("error");
    }
  };

  return (
    <Layout>
      <SeoHead title="Unsubscribe | Semper Fi Pump Service" description="Manage your email preferences." noindex />
      <section className="container py-16 md:py-24 max-w-xl">
        <h1 className="text-3xl font-black mb-6">Unsubscribe</h1>
        {state === "loading" && <p className="text-muted-foreground">Verifying your link…</p>}
        {state === "invalid" && <p className="text-muted-foreground">This unsubscribe link is invalid or expired.</p>}
        {state === "already" && <p className="text-muted-foreground">You've already been unsubscribed.</p>}
        {state === "valid" && (
          <>
            <p className="text-muted-foreground mb-6">Click the button below to confirm you no longer want to receive emails from Semper Fi Pump Service.</p>
            <Button onClick={confirm} size="lg">Confirm Unsubscribe</Button>
          </>
        )}
        {state === "submitting" && <p className="text-muted-foreground">Processing…</p>}
        {state === "done" && <p className="text-foreground font-medium">You've been unsubscribed. You won't receive further emails from us.</p>}
        {state === "error" && <p className="text-destructive">{error}</p>}
      </section>
    </Layout>
  );
};

export default Unsubscribe;
